<template>
  <BaseTopBar v-if="isMounted" is-hideable>
    <template #left>
      <Breadcrumb
        :items="breadcrumbItems"
        :home="{ type: 'workspace', id: workspaceId }"
      />
    </template>
    <template #right>
      <BaseButton
        kind="auxiliary"
        :text="isDatabasePinned ? 'Unpin' : 'Pin'"
        @click="toggleDatabasePin"
      >
        <template #icon>
          <PinIcon :class="{ pinned: isDatabasePinned }" />
        </template>
      </BaseButton>
      <BaseButton kind="auxiliary" text="Schema" @click="openDatabaseSettings">
        <template #icon>
          <SettingsIcon />
        </template>
      </BaseButton>
    </template>
    <template #more>
      <BaseButton
        class="context-action"
        kind="auxiliary"
        text="Copy link"
        is-expanded
        @click="$emit('copyLink')"
      >
        <template #icon>
          <CopyLinkIcon />
        </template>
        <template #right>{{ copyLinkShortcutDisplay }}</template>
      </BaseButton>
      <BaseButton
        v-if="canDelete"
        class="context-action"
        kind="auxiliary"
        text="Delete"
        tint="red"
        is-expanded
        @click="$emit('delete')"
      >
        <template #icon>
          <DeleteIcon />
        </template>
      </BaseButton>
    </template>
  </BaseTopBar>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, toRefs } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import Breadcrumb, { type BreadcrumbItem } from "@/components/Breadcrumb.vue";
import CopyLinkIcon from "@/components/icons/CopyLinkIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import PinIcon from "@/components/icons/PinIcon.vue";
import SettingsIcon from "@/components/icons/tabs/SettingsIcon.vue";
import * as inject from "@/components/inject";
import { getOrganizationIdFromWorkspaceId } from "@/components/workspaces";
import { hotkeys } from "@/utils/hotkeys";
import { keymap } from "@/utils/keymap";
import { navigateToType } from "@/utils/navigate";

export default defineComponent({
  components: {
    BaseButton,
    BaseTopBar,
    Breadcrumb,
    CopyLinkIcon,
    DeleteIcon,
    PinIcon,
    SettingsIcon,
  },
  props: {
    workspaceId: { type: String, required: true },
    databaseId: { type: String, required: true },
    titleScrolledPast: { type: Boolean, default: false },
    databaseTitle: { type: String, default: undefined },
    canDelete: { type: Boolean, default: false },
  },
  emits: {
    copyLink: () => true,
    delete: () => true,
  },
  setup(props) {
    const profile = inject.profile();
    const router = inject.router();
    const menu = inject.menu();
    const { workspaceId, databaseId, titleScrolledPast, databaseTitle } =
      toRefs(props);

    const isMounted = ref(false);
    onMounted(() => (isMounted.value = true));

    const breadcrumbItems = computed((): BreadcrumbItem[] => {
      if (titleScrolledPast.value) {
        return [
          {
            title: databaseTitle.value || "Database",
            link: { params: {} },
            fadeIn: true,
            onClick: () => {
              document
                .getElementById("scrollable-database")
                ?.scrollTo({ top: 0, behavior: "smooth" });
            },
          },
        ];
      }
      return [];
    });

    const isDatabasePinned = computed(() =>
      menu.visibleItems.value.some(
        (item) =>
          item.target.kind === "database" &&
          item.target.workspace_id === workspaceId.value &&
          item.target.table_id === databaseId.value
      )
    );

    const toggleDatabasePin = async () => {
      if (isDatabasePinned.value) {
        const item = menu.visibleItems.value.find(
          (i) =>
            i.target.kind === "database" &&
            i.target.workspace_id === workspaceId.value &&
            i.target.table_id === databaseId.value
        );
        if (item) await menu.unpinDatabase(item);
      } else {
        await menu.pinDatabase(workspaceId.value, databaseId.value);
      }
    };

    const openDatabaseSettings = async () => {
      const orgId = getOrganizationIdFromWorkspaceId(
        profile.value,
        workspaceId.value
      );
      if (!orgId) return;
      await navigateToType(router, orgId, workspaceId.value, databaseId.value);
    };

    const copyLinkShortcutDisplay =
      hotkeys.format.stringify(
        [keymap.keyMod, keymap.keyShift, keymap.keyL],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";

    return {
      breadcrumbItems,
      copyLinkShortcutDisplay,
      isMounted,
      isDatabasePinned,
      openDatabaseSettings,
      toggleDatabasePin,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/context-actions.scss";
</style>
